import React, { useCallback, useMemo, useRef } from "react";
import { Formik } from "formik";
import moment from "moment";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { useFinancial } from "../../../hooks/useFinancialReducer";
import useWindowSize from "../../../hooks/useWindowSize";

import {
  Button,
  Col,
  DatePicker,
  Icon,
  Modal,
  Radio,
  Row,
  Spin,
  Table,
  notification,
} from "antd";
import ButtonStandard from "../../utils/button";
import TagStatus from "../../utils/tagStatus";
import EditInstallmentFinancialTransactionModal from "../editInstallmentFinancialTransactionModal";
import { Container } from "./styles";

// Services
import { useEffect, useState } from "react";
import { pageFinancialTransactionMainInstallments } from "../../../services/financialTransactionMainInstallmentService";
import {
  settleFinancialTransactions,
  unsettleFinancialTransactions,
} from "../../../services/financialTransactionsService";
import { numberMask } from "../../../services/helpersMethodsService";
import { transformCamelCaseToSnakeCase } from "../../../utils/stringUtils";
import {
  getColumnDateWithBetweenSearchProps,
  getColumnNumberWithBetweenSearchProps,
  getColumnSearchProps,
} from "../../../utils/tableUtils";

// import { Container } from './styles';
const Column = Table.Column;

const INITIAL_FORM_STATE = {
  useExpirationDate: false,
  settleDate: null,
};

const validationSchema = Yup.object().shape({
  useExpirationDate: Yup.boolean().required(),
  settleDate: Yup.date()
    .notRequired()
    .nullable()
    .when(["useExpirationDate"], {
      is: (useExpirationDate) => !useExpirationDate,
      then: Yup.date().required(),
      otherwise: Yup.date().notRequired().nullable(),
    }),
});

function SettleFinancialTransactionModal() {
  const formikRef = useRef();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [installmentToEdit, setInstallmentToEdit] = useState(null);
  const [isEditInstallmentModalVisible, setIsEditInstallmentModalVisible] =
    useState(false);
  const [pagination, setPagination] = useState({
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
  });
  const [installmentsEdited, setInstallmentsEdited] = useState([]);

  const {
    refreshListFinancialTransaction,
    selectedFinancialTransactionElements,
    selectedFinancialTransactionKeys: ids,
    shouldShowModalSettledFinancialTransaction: visible,
    modalSettledFinancialTransactionAction: action,
    handleCloseModalSettled,
    handleSelectFinancialTransaction,
  } = useFinancial();
  const { width } = useWindowSize();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const baseSearch = useMemo(() => {
    return action === "remove" ? "is_settled = true" : "is_settled = false";
  }, [action]);

  const rowSelection = {
    selectedRowKeys: ids,
    onSelect: (record, selected, selectedRows, nativeEvent) => {
      if (selected) {
        handleSelectFinancialTransaction([
          ...selectedFinancialTransactionElements,
          record,
        ]);
      } else {
        handleSelectFinancialTransaction(
          selectedFinancialTransactionElements.filter(
            (element) => element.id !== record.id,
          ),
        );
      }
    },
    onSelectAll: (record, selected, selectedRows, nativeEvent) => {
      // Get just the ids
      // const keys = selectedRows.map((sr) => sr.id);
      // Get all the transactions selected unless the unselected
      const lastSelectedTransactions =
        selectedFinancialTransactionElements.filter(
          (sA) => !selectedRows.includes(sA),
        );

      if (record) {
        handleSelectFinancialTransaction([
          ...selectedFinancialTransactionElements,
          ...selectedRows,
        ]);
      } else {
        handleSelectFinancialTransaction(lastSelectedTransactions);
      }
    },
  };

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback(
    (filters) => {
      let search = baseSearch;

      if (filters && Object.entries(filters).length > 0) {
        Object.entries(filters).forEach(([field, value]) => {
          if (value && value.length > 0) {
            const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
            if (fieldS === "value" || fieldS === "installment_number") {
              const operatorS =
                value[1] === "equal"
                  ? "="
                  : value[1] === "greaterThan"
                    ? ">"
                    : value[1] === "greaterOrEqualThan"
                      ? ">="
                      : value[1] === "lessThan"
                        ? "<"
                        : value[1] === "lessOrEqualThan"
                          ? "<="
                          : value[1] === "between"
                            ? "between"
                            : "=";
              search =
                search === ""
                  ? `${fieldS} ${
                      operatorS === "between"
                        ? `${operatorS} ${value[0]} and ${value[2]}`
                        : `${operatorS} ${value[0]}`
                    }`
                  : `${search};${fieldS} ${
                      operatorS === "between"
                        ? `${operatorS} ${value[0]} and ${value[2]}`
                        : `${operatorS} ${value[0]}`
                    }`;
            } else if (
              fieldS === "billing_date" ||
              fieldS === "expiration_date"
            ) {
              const operatorS =
                value[1] === "equal"
                  ? "="
                  : value[1] === "greaterThan"
                    ? ">"
                    : value[1] === "greaterOrEqualThan"
                      ? ">="
                      : value[1] === "lessThan"
                        ? "<"
                        : value[1] === "lessOrEqualThan"
                          ? "<="
                          : value[1] === "between"
                            ? "between"
                            : "=";

              search =
                search === ""
                  ? `cast(${fieldS} as date) ${
                      operatorS === "between"
                        ? `${operatorS} cast('${moment(value[0]).format(
                            "YYYY-MM-DD",
                          )}' as date) and cast('${moment(value[2]).format(
                            "YYYY-MM-DD",
                          )}' as date)`
                        : `${operatorS} cast('${moment(value[0]).format(
                            "YYYY-MM-DD",
                          )}' as date)`
                    } `
                  : `${search};cast(${fieldS} as date) ${
                      operatorS === "between"
                        ? `${operatorS} cast('${moment(value[0]).format(
                            "YYYY-MM-DD",
                          )}' as date) and cast('${moment(value[2]).format(
                            "YYYY-MM-DD",
                          )}' as date)`
                        : `${operatorS} cast('${moment(value[0]).format(
                            "YYYY-MM-DD",
                          )}' as date)`
                    }`;
            } else if (fieldS === "transaction_type" || fieldS === "origin") {
              if (fieldS === "transaction_type" && value === "all") {
                search =
                  search === ""
                    ? `${fieldS} in ('expense', 'income')`
                    : `${search};${fieldS} in ('expense', 'income')`;
              } else {
                search =
                  search === ""
                    ? `${fieldS} in (${value
                        .map((o) => (o = `'${o}'`))
                        .join(", ")})`
                    : `${search};${fieldS} in (${value
                        .map((o) => (o = `'${o}'`))
                        .join(", ")})`;
              }
            } else if (fieldS === "status") {
              if (value[0] === "conciliated") {
                search =
                  search === ""
                    ? `conciliation_status = 'conciliated'`
                    : `${search};conciliation_status = 'conciliated'`;
              } else if (value[0] === "partial_conciliated") {
                search =
                  search === ""
                    ? `conciliation_status = 'partial_conciliated'`
                    : `${search};conciliation_status = 'partial_conciliated'`;
              } else {
                search =
                  search === ""
                    ? `is_settled = ${value[0] === "true"}`
                    : `${search};is_settled = ${value[0] === "true"}`;
              }
            } else {
              search =
                search === ""
                  ? `upper(${fieldS}) like upper('%25${value}%25')`
                  : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
            }
          }
        });
      }

      return search !== "" ? search : "";
    },
    [baseSearch],
  );

  const fetchData = useCallback(
    async (page = 0, size = 10, sort = {}, search = "") => {
      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await pageFinancialTransactionMainInstallments({
          groupId,
          farmId,
          size,
          sort,
          search,
          page,
        });

        setData(results);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        notification.error({
          message: translation.financial.settleModal.messages.fetchError,
        });
      }
    },
    [farmId, groupId, translation.financial.settleModal.messages.fetchError],
  );

  const onTableChange = useCallback(
    (pagination, filters, sorter) => {
      if (pagination && filters && sorter) {
        setPagination({
          page: pagination.current,
          size: pagination.pageSize,
          tableFilters: filters,
          tableSorters: getSortString(sorter),
          search: getSearchString(filters),
        });
        fetchData(
          pagination.current,
          pagination.pageSize,
          getSortString(sorter),
          getSearchString(filters),
        );
      } else {
        setPagination({
          page: 0,
          size: 10,
          tableFilters: {},
          tableSorters: {},
          search: baseSearch,
        });
        fetchData(0, 10, {}, baseSearch);
      }
    },
    [baseSearch, fetchData, getSearchString, getSortString],
  );

  const updateTransactionsToSettled = useCallback(
    async (values) => {
      setIsLoading(true);
      try {
        const financialTransactionMainIds = [
          ...new Set(
            selectedFinancialTransactionElements.map(
              (element) => element.financialTransactionMainId,
            ),
          ),
        ];
        const body = {
          settleDate: values.settleDate,
          useExpirationDate: values.useExpirationDate,
          financialTransactionIds: ids,
          financialTransactionMainIds,
          financialTransactionUpdates: installmentsEdited,
        };
        await settleFinancialTransactions({
          groupId,
          farmId,
          body,
        });
        formikRef.current.resetForm(INITIAL_FORM_STATE);
        notification.success({
          message: translation.financial.settleModal.messages.settleSuccess,
        });
        setPagination({
          total: null,
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: baseSearch,
        });
        handleSelectFinancialTransaction([]);
        refreshListFinancialTransaction();
        setIsLoading(false);
        handleCloseModalSettled();
      } catch (error) {
        notification.error({
          message: translation.financial.settleModal.messages.settleError,
        });
        setIsLoading(false);
      }
    },
    [
      selectedFinancialTransactionElements,
      ids,
      installmentsEdited,
      groupId,
      farmId,
      translation.financial.settleModal.messages.settleSuccess,
      translation.financial.settleModal.messages.settleError,
      baseSearch,
      handleSelectFinancialTransaction,
      refreshListFinancialTransaction,
      handleCloseModalSettled,
    ],
  );

  const updateTransactionsToUnsettled = useCallback(
    async (values) => {
      setIsLoading(true);
      try {
        const financialTransactionMainIds = [
          ...new Set(
            selectedFinancialTransactionElements.map(
              (element) => element.financialTransactionMainId,
            ),
          ),
        ];
        const body = {
          financialTransactionIds: ids,
          financialTransactionMainIds,
        };
        await unsettleFinancialTransactions({
          groupId,
          farmId,
          body,
        });
        formikRef.current.resetForm(INITIAL_FORM_STATE);
        notification.success({
          message: translation.financial.settleModal.messages.unsettleSuccess,
        });
        setPagination({
          total: null,
          size: 10,
          page: 0,
          tableFilters: {},
          tableSorters: {},
          search: baseSearch,
        });
        handleSelectFinancialTransaction([]);
        refreshListFinancialTransaction();
        setIsLoading(false);
        handleCloseModalSettled();
      } catch (error) {
        notification.error({
          message: translation.financial.settleModal.messages.unsettleError,
        });
        setIsLoading(false);
      }
    },
    [
      selectedFinancialTransactionElements,
      ids,
      groupId,
      farmId,
      translation.financial.settleModal.messages.unsettleSuccess,
      translation.financial.settleModal.messages.unsettleError,
      baseSearch,
      handleSelectFinancialTransaction,
      refreshListFinancialTransaction,
      handleCloseModalSettled,
    ],
  );

  const handleSearch = useCallback((selectedKeys, confirm, dataIndex) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleEditInstallment = useCallback(
    (record) => {
      const findIndex = installmentsEdited.findIndex(
        (value) => value.financialTransactionInstallmentId === record.id,
      );
      if (findIndex >= 0) {
        setInstallmentToEdit({ ...record, ...installmentsEdited[findIndex] });
      } else {
        setInstallmentToEdit(record);
      }
      setIsEditInstallmentModalVisible(true);
    },
    [installmentsEdited],
  );

  const handleCancelEditInstallment = useCallback(() => {
    setIsEditInstallmentModalVisible(false);
    setInstallmentToEdit(null);
  }, []);

  const handleSaveEditInstallment = useCallback(
    (values) => {
      setIsEditInstallmentModalVisible(false);
      setInstallmentToEdit(null);
      const findIndex = installmentsEdited.findIndex(
        (value) =>
          value.financialTransactionInstallmentId ===
          values.financialTransactionInstallmentId,
      );
      if (findIndex < 0) setInstallmentsEdited((prev) => [...prev, values]);
      setInstallmentsEdited((prev) =>
        prev.map((installment) => {
          if (
            installment.financialTransactionInstallmentId ===
            values.financialTransactionInstallmentI
          ) {
            return values;
          }
          return installment;
        }),
      );
    },
    [installmentsEdited],
  );

  const handleCancel = useCallback(() => {
    setPagination({
      total: null,
      size: 10,
      page: 0,
      tableFilters: {},
      tableSorters: {},
      search: baseSearch,
    });
    formikRef.current.resetForm(INITIAL_FORM_STATE);
    handleCloseModalSettled();
  }, [baseSearch, handleCloseModalSettled]);

  const handleSubmit = useCallback(
    (values) => {
      if (selectedFinancialTransactionElements.length === 0) {
        notification.warn({
          message: translation.financial.settleModal.messages.selectFirst,
        });
      } else {
        if (action === "new") {
          const settleDate = values.settleDate;
          if (
            selectedFinancialTransactionElements.filter(
              (financialTransaction) =>
                moment(financialTransaction.billingDate).isAfter(settleDate),
            ).length > 0
          ) {
            const numberOfTransactionsWithBillingDateBeforeSettleDate =
              selectedFinancialTransactionElements.filter(
                (financialTransaction) =>
                  moment(financialTransaction.billingDate).isAfter(settleDate),
              ).length;
            Modal.error({
              centered: true,
              width: 550,
              title: translation.financial.settleModal.messages.errorTitle,
              content: `${translation.financial.settleModal.messages.dateIncompatibleError.replace("{{count}}", numberOfTransactionsWithBillingDateBeforeSettleDate)}`,
            });
            return;
          } else {
            if (
              selectedFinancialTransactionElements.filter(
                (financialTransaction) =>
                  financialTransaction.isSettled === true,
              ).length > 0
            ) {
              Modal.confirm({
                centered: true,
                width: 550,
                title:
                  translation.financial.settleModal.messages
                    .alreadySettledWarningTitle,
                content:
                  translation.financial.settleModal.messages
                    .alreadySettledWarningContent,
                okText:
                  translation.financial.settleModal.messages
                    .alreadySettledWarningOk,
                onOk: () => {
                  return;
                },
                cancelText:
                  translation.financial.settleModal.messages
                    .alreadySettledWarningCancel,
                onCancel: () => {
                  updateTransactionsToSettled(values);
                },
              });
            } else {
              updateTransactionsToSettled(values);
            }
          }
        } else {
          if (
            selectedFinancialTransactionElements.filter(
              (financialTransaction) =>
                financialTransaction.isSettled === false,
            ).length > 0
          ) {
            Modal.error({
              centered: true,
              width: 500,
              content:
                translation.financial.settleModal.messages
                  .selectOnlySettledError,
            });

            return;
          } else if (
            selectedFinancialTransactionElements.filter(
              (financialTransaction) =>
                financialTransaction.isSettled === true &&
                financialTransaction.isPaidPartially === true,
            ).length > 0
          ) {
            Modal.error({
              centered: true,
              width: 500,
              content:
                translation.financial.settleModal.messages
                  .selectOnlyNotPartiallyPaidError,
            });

            return;
          }
          updateTransactionsToUnsettled(values);
        }
      }
    },
    [
      action,
      selectedFinancialTransactionElements,
      translation.financial.settleModal.messages.alreadySettledWarningCancel,
      translation.financial.settleModal.messages.alreadySettledWarningContent,
      translation.financial.settleModal.messages.alreadySettledWarningOk,
      translation.financial.settleModal.messages.alreadySettledWarningTitle,
      translation.financial.settleModal.messages.dateIncompatibleError,
      translation.financial.settleModal.messages.errorTitle,
      translation.financial.settleModal.messages.selectFirst,
      translation.financial.settleModal.messages
        .selectOnlyNotPartiallyPaidError,
      translation.financial.settleModal.messages.selectOnlySettledError,
      updateTransactionsToSettled,
      updateTransactionsToUnsettled,
    ],
  );

  const TableListOfFinancialTransactions = (
    <Table
      rowClassName={(record) =>
        record.transactionType === "expense"
          ? "table-row-red"
          : "table-row-green"
      }
      rowKey="id"
      size="small"
      dataSource={data != null ? data.content : []}
      onChange={onTableChange}
      pagination={{
        current: pagination.page,
        total: data != null ? data.totalElements : 0,
        size: pagination.size || 10,
        hideOnSinglePage: pagination.size > 10 ? false : true,
        pageSize: pagination.size,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
      }}
      scroll={{
        x: true,
      }}
      rowSelection={rowSelection}
      style={{ width: "100%", margin: "0" }}
    >
      {action !== "remove" && (
        <Column
          render={(record) => {
            return (
              <Button
                icon="edit"
                disabled={!ids.includes(record.id)}
                onClick={() => handleEditInstallment(record)}
              />
            );
          }}
        />
      )}
      <Column
        title={translation.financial.transactions.table.columns.isSettled}
        dataIndex="isSettled"
        key="isSettled"
        width={120}
        align="left"
        render={(text, record) =>
          record.isSettled === false ? (
            <TagStatus
              background="#E4F4FF"
              borderColor="#4A85AE"
              color="#4A85AE"
            >
              {translation.financial.table.tagOpen}
            </TagStatus>
          ) : (
            <TagStatus
              background="#E1FFE4"
              borderColor="#106518"
              color="#106518"
            >
              {translation.financial.table.tagSettled}
            </TagStatus>
          )
        }
      />
      {action === "remove" && (
        <Column
          title={translation.financial.transactions.table.columns.settleDate}
          dataIndex="settleDate"
          key="settleDate"
          align="left"
          sortOrder={
            pagination.tableSorters?.columnKey === "settleDate" &&
            pagination.tableSorters?.order
          }
          filteredValue={pagination.tableFilters?.settleDate || null}
          {...getColumnDateWithBetweenSearchProps(
            "settleDate",
            handleReset,
            handleSearch,
          )}
          render={(text) => (text ? moment(text).format("DD/MM/YYYY") : null)}
        />
      )}
      <Column
        title={translation.financial.transactions.table.columns.transactionType}
        dataIndex="transactionType"
        key="transactionType"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "transactionType" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.transactionType || null}
        filtered
        filters={[
          {
            text: "Todos",
            value: "all",
          },
          {
            text: "Despesa",
            value: "expense",
          },
          {
            text: "Receita",
            value: "income",
          },
        ]}
        filterMultiple={false}
        render={(text) =>
          text === "expense" ? <span>D</span> : <span>R</span>
        }
      />

      <Column
        title={translation.financial.transactions.table.columns.documentNumber}
        dataIndex="documentNumber"
        key="documentNumber"
        align="left"
        width={200}
        sortOrder={
          pagination.tableSorters?.columnKey === "documentNumber" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.documentNumber || null}
        {...getColumnSearchProps("documentNumber", handleReset, handleSearch)}
      />
      <Column
        title={translation.financial.transactions.table.columns.entityName}
        dataIndex="entityName"
        key="entityName"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "entityName" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.entityName || null}
        {...getColumnSearchProps("entityName", handleReset, handleSearch)}
      />
      <Column
        title={translation.financial.transactions.table.columns.installment}
        dataIndex="installmentNumber"
        key="installmentNumber"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "installmentNumber" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.installmentNumber || null}
        {...getColumnNumberWithBetweenSearchProps(
          "installmentNumber",
          handleReset,
          handleSearch,
        )}
        render={(text, record) => (
          <span>{`${record.installmentNumber}/${record.installmentTotal}`}</span>
        )}
      />
      <Column
        title={translation.financial.transactions.table.columns.billingDate}
        dataIndex="billingDate"
        key="billingDate"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "billingDate" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.billingDate || null}
        {...getColumnDateWithBetweenSearchProps(
          "billingDate",
          handleReset,
          handleSearch,
        )}
        render={(text) => <span>{moment(text).format("DD/MM/YYYY")}</span>}
      />

      <Column
        title={translation.financial.transactions.table.columns.expirationDate}
        dataIndex="expirationDate"
        key="expirationDate"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "expirationDate" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.expirationDate || null}
        {...getColumnDateWithBetweenSearchProps(
          "expirationDate",
          handleReset,
          handleSearch,
        )}
        render={(text) => <span>{moment(text).format("DD/MM/YYYY")}</span>}
      />
      <Column
        title={translation.financial.transactions.table.columns.isPaidPartially}
        dataIndex="isPaidPartially"
        key="isPaidPartially"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "isPaidPartially" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.isPaidPartially || null}
        filtered
        filters={[
          {
            text: "Todos",
            value: "all",
          },
          {
            text: "Parc. Pago",
            value: "true",
          },
          {
            text: "Pago",
            value: "false",
          },
        ]}
        filterMultiple={false}
        onFilter={(value, record) => {
          return value === "all"
            ? true
            : value === "true"
              ? record.isPaidPartially === true
              : record.isPaidPartially === false;
        }}
        render={(text, record) => (text === true ? <Icon type="close" /> : "")}
      />

      <Column
        title={translation.financial.transactions.table.columns.value}
        dataIndex="value"
        key="value"
        align="right"
        sortOrder={
          pagination.tableSorters?.columnKey === "value" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.value || null}
        {...getColumnNumberWithBetweenSearchProps(
          "value",
          handleReset,
          handleSearch,
        )}
        render={(text, record) => (
          <span>{numberMask(record.value || 0, true)}</span>
        )}
      />

      <Column
        title={
          translation.financial.transactions.table.columns.bankAccountFarmName
        }
        dataIndex="bankAccountFarmName"
        key="bankAccountFarmName"
        align="left"
        sortOrder={
          pagination.tableSorters?.columnKey === "bankAccountFarmName" &&
          pagination.tableSorters?.order
        }
        filteredValue={pagination.tableFilters?.bankAccountFarmName || null}
        {...getColumnSearchProps(
          "bankAccountFarmName",
          handleReset,
          handleSearch,
        )}
        render={(text, record) => (
          <span>{record.bankAccountFarmName || "-"}</span>
        )}
      />
    </Table>
  );

  useEffect(() => {
    if (visible === true) {
      setPagination({
        page: 0,
        size: 10,
        tableFilters: {},
        tableSorters: {},
        search: baseSearch,
      });
      setInstallmentsEdited([]);
      fetchData(0, 10, {}, baseSearch);
    }
    return () => {
      setPagination({
        page: 0,
        size: 10,
        tableFilters: {},
        tableSorters: {},
        search: baseSearch,
      });
      setInstallmentsEdited([]);
      setData([]);
    };
  }, [baseSearch, fetchData, visible]);

  return (
    <Modal
      visible={visible}
      width={width >= 1440 ? 1350 : 800}
      title={
        action === "new" ? "Dar baixa nas movimentações" : "Remover baixa(s)"
      }
      centered
      closable={true}
      onCancel={handleCancel}
      footer={null}
    >
      <Formik
        ref={formikRef}
        initialValues={INITIAL_FORM_STATE}
        validationSchema={action === "new" ? validationSchema : null}
        onSubmit={handleSubmit}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Spin spinning={isLoading}>
              <Container>
                <Row type="flex" justify="center" align="middle">
                  <Col>
                    {action === "new" ? (
                      <p>
                        Ao clicar em confirmar, as movimentações listadas abaixo
                        com o status{" "}
                        <TagStatus
                          background="#E4F4FF"
                          borderColor="#4A85AE"
                          color="#4A85AE"
                        >
                          {translation.financial.table.tagOpen}
                        </TagStatus>{" "}
                        serão atualizadas para{" "}
                        <TagStatus
                          background="#E1FFE4"
                          borderColor="#106518"
                          color="#106518"
                        >
                          {translation.financial.table.tagSettled}
                        </TagStatus>{" "}
                        na data informada no campo "Data da Baixa", ou caso
                        habilitado o campo "Utilizar data de vencimento como
                        data de baixa?", será utilizado a data de vencimento
                        como a data da baixa.
                      </p>
                    ) : (
                      <p>
                        Ao clicar em confirmar, as movimentações listadas abaixo
                        com o status{" "}
                        <TagStatus
                          background="#E1FFE4"
                          borderColor="#106518"
                          color="#106518"
                        >
                          {translation.financial.table.tagSettled}
                        </TagStatus>{" "}
                        serão atualizadas para{" "}
                        <TagStatus
                          background="#E4F4FF"
                          borderColor="#4A85AE"
                          color="#4A85AE"
                        >
                          {translation.financial.table.tagOpen}
                        </TagStatus>
                        .
                      </p>
                    )}
                  </Col>
                </Row>
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                {action === "new" && (
                  <Row
                    type="flex"
                    gutter={16}
                    style={{ marginBottom: 8, marginTop: 8 }}
                  >
                    <Col>
                      <Row>
                        <label>
                          Utilizar data de vencimento como data de baixa?
                        </label>
                      </Row>
                      <Row>
                        <Radio.Group
                          name="useExpirationDate"
                          style={{ color: "#4b4b4b" }}
                          value={values.useExpirationDate}
                          onChange={(e) => {
                            setFieldValue("useExpirationDate", e.target.value);
                            setFieldValue("settleDate", null);
                          }}
                        >
                          <Radio value={true}>{translation.buttons.yes}</Radio>
                          <Radio value={false}>{translation.buttons.no}</Radio>
                        </Radio.Group>
                      </Row>
                    </Col>
                    <Col>
                      <Row>
                        <label
                          className={
                            errors.settleDate && submitCount > 0 ? "error" : ""
                          }
                        >
                          Data da baixa
                          {values.useExpirationDate === false && "*"}
                        </label>
                      </Row>
                      <Row>
                        <DatePicker
                          name="settleDate"
                          value={values.settleDate}
                          format={"DD/MM/YYYY"}
                          disabledDate={(current) => current.isAfter(moment())}
                          disabled={values.useExpirationDate}
                          onChange={(date) => setFieldValue("settleDate", date)}
                        />
                      </Row>
                    </Col>
                  </Row>
                )}
                <Row type="flex">
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    {TableListOfFinancialTransactions}
                  </Col>
                </Row>
                {/* Footer */}
                <Row
                  type="flex"
                  justify="center"
                  align="middle"
                  gutter={16}
                  style={{ marginTop: 16 }}
                >
                  <Col>
                    <ButtonStandard
                      type="button"
                      buttonType="type4"
                      size="s"
                      onClick={handleCancel}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                  </Col>
                  <Col>
                    <ButtonStandard type="submit" buttonType="type1" size="s">
                      {translation.buttons.confirm}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Container>
            </Spin>
          </form>
        )}
      />
      <EditInstallmentFinancialTransactionModal
        visible={isEditInstallmentModalVisible}
        record={installmentToEdit}
        onSave={handleSaveEditInstallment}
        onCancel={handleCancelEditInstallment}
      />
    </Modal>
  );
}

export default SettleFinancialTransactionModal;
